import { useState, useEffect, useCallback } from "react";
import { api, formatCurrency, formatDate, getErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { MaintenanceCrossLinksFields } from "@/components/equipment/MaintenanceCrossLinksFields";
import { LinkedSuppliersBlock } from "@/components/equipment/LinkedSuppliersBlock";
import {
  PieChart, Pie, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, Cell
} from "recharts";
import {
  Plus, Pencil, Trash2, Loader2, Package, Car, HardHat, Shirt, Monitor, Wrench,
  AlertTriangle, Eye, ChevronDown, ChevronUp, ArrowUpDown, BarChart3, Archive, ArchiveRestore
} from "lucide-react";

const CATEGORIES = [
  { id: "bureau", label: "Matériel de bureau", icon: Monitor },
  { id: "petit_materiel", label: "Petit matériel", icon: Wrench },
  { id: "gros_materiel", label: "Gros matériel", icon: Package },
  { id: "vehicules", label: "Véhicules", icon: Car },
  { id: "vetements", label: "Vêtements", icon: Shirt },
  { id: "epi", label: "EPI", icon: HardHat },
];

const PIE_COLORS = ["#C05621", "#2D3748", "#F6AD55", "#3182CE", "#38A169", "#805AD5"];
const BAR_COLORS = ["#C05621", "#2D3748", "#F6AD55", "#3182CE", "#38A169", "#805AD5"];

const POWER_SOURCES = [
  { value: "manuel", label: "Manuel (sans énergie)" },
  { value: "electrique_secteur", label: "Électrique — secteur" },
  { value: "electrique_batterie", label: "Électrique — batterie" },
  { value: "thermique", label: "Thermique (carburant)" },
  { value: "hybride", label: "Hybride" },
  { value: "autre", label: "Autre" },
];

const FUEL_TYPES = [
  { value: "essence_sp95", label: "Essence SP95" },
  { value: "essence_sp98", label: "Essence SP98" },
  { value: "essence_e85", label: "Essence E85" },
  { value: "diesel", label: "Diesel" },
  { value: "gpl", label: "GPL" },
  { value: "ethanol", label: "Éthanol" },
  { value: "autre", label: "Autre" },
];

const POWER_LABEL = Object.fromEntries(POWER_SOURCES.map(p => [p.value, p.label]));
const FUEL_LABEL = Object.fromEntries(FUEL_TYPES.map(f => [f.value, f.label]));

const emptyForm = {
  name: "", brand: "", material_type: "", year: "", serial_number: "",
  power_source: "", fuel_type: "",
  maintenance_company: "",
  purchase_price: "", purchase_price_is_ttc: "ttc",
  vat_rate: "20", vat_recoverable: false,
  purchase_location: "", purchase_date: "", first_registration_date: "", comments: "",
  expiry_date: "", front_tire_size: "", rear_tire_size: "", registration: "", engine: "",
  battery_reference: "", battery_capacity: "",
  vehicle_usage: "chantier"
};

export const EquipmentPage = () => {
  const [activeTab, setActiveTab] = useState("bureau");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [formData, setFormData] = useState({ ...emptyForm });
  const [detailItem, setDetailItem] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [alerts, setAlerts] = useState({ vehicle_alerts: [], epi_alerts: [], maintenance_alerts: [], total_count: 0 });
  const [dashboard, setDashboard] = useState(null);
  const [sortBy, setSortBy] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const [showArchived, setShowArchived] = useState(false);
  // Vehicle sub-dialogs
  const [inspectionDialogOpen, setInspectionDialogOpen] = useState(false);
  const [maintenanceDialogOpen, setMaintenanceDialogOpen] = useState(false);
  const [inspForm, setInspForm] = useState({ inspection_type: "technique", inspection_date: "", expiry_date: "", notes: "" });
  const [maintForm, setMaintForm] = useState({ maintenance_date: "", description: "", cost: "", cost_is_ttc: "ttc", vat_rate: "20", location: "", notes: "", supplier_id: null, supplier_product_id: null, reason_id: null, next_maintenance_date: "" });

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      const promises = [
        api.getEquipment({ category: activeTab === "inventaire" ? undefined : activeTab, sort_by: sortBy, sort_dir: sortDir, include_archived: showArchived }),
        api.getEquipmentAlerts()
      ];
      if (activeTab === "inventaire") {
        promises.push(api.getInventoryDashboard());
      }
      const results = await Promise.all(promises);
      setItems(results[0].data);
      setAlerts(results[1].data);
      if (results[2]) setDashboard(results[2].data);
    } catch (e) {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, [activeTab, sortBy, sortDir, showArchived]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleArchiveToggle = async (item) => {
    try {
      if (item.archived) {
        await api.unarchiveEquipment(item.id);
        toast.success("Matériel restauré");
      } else {
        await api.archiveEquipment(item.id);
        toast.success("Matériel archivé");
      }
      fetchItems();
    } catch {
      toast.error("Erreur archivage");
    }
  };

  const openCreate = () => {
    setEditingItem(null);
    setFormData({ ...emptyForm });
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name || "", brand: item.brand || "", material_type: item.material_type || "",
      year: item.year || "", serial_number: item.serial_number || "",
      power_source: item.power_source || "", fuel_type: item.fuel_type || "",
      maintenance_company: item.maintenance_company || "",
      purchase_price: item.purchase_price || "",
      purchase_price_is_ttc: item.purchase_price_is_ttc === false ? "ht" : "ttc",
      vat_rate: item.vat_rate != null ? String(item.vat_rate) : "20",
      vat_recoverable: !!item.vat_recoverable,
      purchase_location: item.purchase_location || "",
      purchase_date: item.purchase_date || "",
      first_registration_date: item.first_registration_date || "",
      comments: item.comments || "",
      expiry_date: item.expiry_date || "", front_tire_size: item.front_tire_size || "",
      rear_tire_size: item.rear_tire_size || "", registration: item.registration || "",
      engine: item.engine || "",
      battery_reference: item.battery_reference || "",
      battery_capacity: item.battery_capacity || "",
      vehicle_usage: item.vehicle_usage || "chantier"
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) { toast.error("Le nom est requis"); return; }
    try {
      const payload = {
        ...formData,
        category: activeTab,
        year: formData.year ? parseInt(formData.year) : null,
        purchase_price: formData.purchase_price ? parseFloat(formData.purchase_price) : null,
        purchase_price_is_ttc: formData.purchase_price ? formData.purchase_price_is_ttc === "ttc" : null,
        vat_rate: formData.purchase_price && formData.vat_rate !== "" ? parseFloat(formData.vat_rate) : null,
        vat_recoverable: formData.purchase_price ? !!formData.vat_recoverable : null,
        first_registration_date: (activeTab === "vehicules" || activeTab === "gros_materiel") ? (formData.first_registration_date || null) : null,
        vehicle_usage: activeTab === "vehicules" ? (formData.vehicle_usage || "chantier") : null,
      };
      if (editingItem) {
        await api.updateEquipment(editingItem.id, payload);
        toast.success("Matériel modifié");
      } else {
        await api.createEquipment(payload);
        toast.success("Matériel ajouté");
      }
      setDialogOpen(false);
      fetchItems();
    } catch (e) {
      toast.error(getErrorMessage(e, "Erreur"));
    }
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;
    try {
      await api.deleteEquipment(itemToDelete.id);
      toast.success("Matériel supprimé");
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      fetchItems();
    } catch (e) {
      toast.error(getErrorMessage(e, "Erreur"));
    }
  };

  const openDetail = async (item) => {
    try {
      const res = await api.getEquipmentItem(item.id);
      setDetailItem(res.data);
      setDetailOpen(true);
    } catch (e) {
      toast.error("Erreur de chargement");
    }
  };

  const handleAddInspection = async (e) => {
    e.preventDefault();
    if (!detailItem) return;
    try {
      await api.addVehicleInspection(detailItem.id, inspForm);
      toast.success("Contrôle ajouté");
      setInspectionDialogOpen(false);
      setInspForm({ inspection_type: "technique", inspection_date: "", expiry_date: "", notes: "" });
      const res = await api.getEquipmentItem(detailItem.id);
      setDetailItem(res.data);
      fetchItems();
    } catch (e) { toast.error(getErrorMessage(e, "Erreur")); }
  };

  const handleAddMaintenance = async (e) => {
    e.preventDefault();
    if (!detailItem) return;
    try {
      await api.addVehicleMaintenance(detailItem.id, {
        ...maintForm,
        cost: parseFloat(maintForm.cost) || 0,
        cost_is_ttc: maintForm.cost ? maintForm.cost_is_ttc === "ttc" : null,
        vat_rate: maintForm.cost && maintForm.vat_rate !== "" ? parseFloat(maintForm.vat_rate) : null,
        next_maintenance_date: maintForm.next_maintenance_date || null,
      });
      toast.success("Entretien ajouté");
      setMaintenanceDialogOpen(false);
      setMaintForm({ maintenance_date: "", description: "", cost: "", cost_is_ttc: "ttc", vat_rate: "20", location: "", notes: "", supplier_id: null, supplier_product_id: null, reason_id: null, next_maintenance_date: "" });
      const res = await api.getEquipmentItem(detailItem.id);
      setDetailItem(res.data);
    } catch (e) { toast.error(getErrorMessage(e, "Erreur")); }
  };

  const toggleSort = (field) => {
    if (sortBy === field) { setSortDir(d => d === "asc" ? "desc" : "asc"); }
    else { setSortBy(field); setSortDir("asc"); }
  };

  const catAlertCount = (catId) => {
    if (catId === "vehicules") return alerts.vehicle_alerts.length;
    if (catId === "epi") return alerts.epi_alerts.length;
    return 0;
  };

  const SortHeader = ({ field, children }) => (
    <th className="cursor-pointer select-none" onClick={() => toggleSort(field)}>
      <span className="flex items-center gap-1">{children}
        {sortBy === field && (sortDir === "asc" ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)}
        {sortBy !== field && <ArrowUpDown className="h-3 w-3 opacity-30" />}
      </span>
    </th>
  );

  return (
    <div className="space-y-6 animate-in" data-testid="equipment-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold">Matériel</h1>
          <p className="text-muted-foreground mt-1">Inventaire et gestion du matériel</p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none" data-testid="show-archived-equipment-toggle">
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} className="h-4 w-4" />
            Afficher archivés
          </label>
          <Button onClick={openCreate} data-testid="add-equipment-btn">
            <Plus className="h-4 w-4 mr-2" />Ajouter
          </Button>
        </div>
      </div>

      {/* Alerts banner */}
      {alerts.total_count > 0 && (
        <Card className="border-2 border-amber-300 bg-amber-50" data-testid="equipment-alerts-banner">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0" />
              <div className="text-sm">
                <span className="font-medium text-amber-800">{alerts.total_count} alerte(s) :</span>{" "}
                {alerts.vehicle_alerts.length > 0 && <span>{alerts.vehicle_alerts.length} contrôle(s) véhicule </span>}
                {alerts.epi_alerts.length > 0 && <span>{alerts.epi_alerts.length} EPI </span>}
                {(alerts.vehicle_alerts.length > 0 || alerts.epi_alerts.length > 0) && (
                  <span className="text-amber-700">expir{(alerts.vehicle_alerts.length + alerts.epi_alerts.length) > 1 ? "ent" : "e"} dans moins de 3 mois ou sont expirés</span>
                )}
                {(alerts.maintenance_alerts || []).length > 0 && (
                  <span data-testid="maintenance-alerts-summary">
                    {(alerts.vehicle_alerts.length > 0 || alerts.epi_alerts.length > 0) ? " · " : ""}
                    <span className="font-medium text-amber-800">
                      {alerts.maintenance_alerts.length} entretien(s)
                    </span>{" "}
                    <span className="text-amber-700">
                      prévu(s) dans les 30 prochains jours
                      {alerts.maintenance_alerts.some(m => m.status === "overdue")
                        ? " (dont en retard)" : ""}
                    </span>
                  </span>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex flex-wrap h-auto gap-1" data-testid="equipment-tabs">
          {CATEGORIES.map(cat => (
            <TabsTrigger key={cat.id} value={cat.id} className="flex items-center gap-1.5 text-xs sm:text-sm" data-testid={`tab-${cat.id}`}>
              <cat.icon className="h-4 w-4" />
              <span className="hidden sm:inline">{cat.label}</span>
              <span className="sm:hidden">{cat.label.split(" ").pop()}</span>
              {catAlertCount(cat.id) > 0 && (
                <Badge variant="destructive" className="h-5 min-w-5 text-xs px-1">{catAlertCount(cat.id)}</Badge>
              )}
            </TabsTrigger>
          ))}
          <TabsTrigger value="inventaire" className="flex items-center gap-1.5 text-xs sm:text-sm" data-testid="tab-inventaire">
            <BarChart3 className="h-4 w-4" />
            <span>Inventaire</span>
          </TabsTrigger>
        </TabsList>

        {CATEGORIES.map(cat => (
          <TabsContent key={cat.id} value={cat.id}>
            <Card>
              <CardContent className="p-0">
                {loading ? (
                  <div className="flex items-center justify-center h-48"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
                ) : items.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <cat.icon className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p>Aucun matériel dans cette catégorie</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <SortHeader field="name">Nom</SortHeader>
                          <th>Marque</th>
                          <SortHeader field="material_type">Type</SortHeader>
                          <SortHeader field="year">Année</SortHeader>
                          <SortHeader field="purchase_price">Prix</SortHeader>
                          <SortHeader field="purchase_date">Date achat</SortHeader>
                          <th>N° série</th>
                          <th>Énergie</th>
                          {cat.id === "vehicules" && <th>Immatriculation</th>}
                          {cat.id === "vehicules" && <th>Usage</th>}
                          {cat.id === "epi" && <th>Fin validité</th>}
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map(item => {
                          const hasAlert = cat.id === "vehicules"
                            ? alerts.vehicle_alerts.some(a => a.equipment_id === item.id)
                            : cat.id === "epi" && item.expiry_date
                              ? alerts.epi_alerts.some(a => a.equipment_id === item.id)
                              : false;
                          return (
                            <tr key={item.id} className={`${hasAlert ? "bg-amber-50" : ""} ${item.archived ? "opacity-60" : ""}`} data-testid={`equipment-row-${item.id}`}>
                              <td className="font-medium">
                                {hasAlert && <AlertTriangle className="h-4 w-4 text-amber-500 inline mr-1" />}
                                {item.name}
                                {item.archived && <span className="ml-2 inline-flex items-center rounded border border-slate-400 px-1.5 py-0.5 text-[10px] text-slate-600" data-testid={`archived-badge-eq-${item.id}`}>Archivé</span>}
                              </td>
                              <td>{item.brand || "-"}</td>
                              <td>{item.material_type || "-"}</td>
                              <td>{item.year || "-"}</td>
                              <td className="font-mono">
                                {item.purchase_price ? (
                                  <div className="flex flex-col leading-tight">
                                    <span>{formatCurrency(item.purchase_price)}<span className="text-xs text-muted-foreground ml-1">{item.purchase_price_is_ttc === false ? "HT" : item.purchase_price_is_ttc === true ? "TTC" : ""}</span></span>
                                    {item.vat_recoverable && item.vat_rate != null && (
                                      <span className="text-xs text-green-700" data-testid={`eq-real-ht-${item.id}`}>
                                        = {formatCurrency(
                                          item.purchase_price_is_ttc === false
                                            ? item.purchase_price
                                            : item.purchase_price / (1 + (item.vat_rate || 0) / 100)
                                        )} HT récupérable
                                      </span>
                                    )}
                                  </div>
                                ) : "-"}
                              </td>
                              <td>{item.purchase_date ? formatDate(item.purchase_date) : "-"}</td>
                              <td className="font-mono text-xs">{item.serial_number || "-"}</td>
                              <td className="text-xs">
                                {item.power_source
                                  ? <span className="whitespace-nowrap">{POWER_LABEL[item.power_source]}{item.fuel_type ? ` · ${FUEL_LABEL[item.fuel_type]}` : ""}</span>
                                  : "-"}
                              </td>
                              {cat.id === "vehicules" && <td className="font-mono">{item.registration || "-"}</td>}
                              {cat.id === "vehicules" && (
                                <td>
                                  <Badge
                                    variant="outline"
                                    className={
                                      (item.vehicle_usage || "chantier") === "administratif"
                                        ? "border-slate-400 text-slate-700 bg-slate-50"
                                        : "border-orange-400 text-orange-700 bg-orange-50"
                                    }
                                    data-testid={`vehicle-usage-${item.id}`}
                                  >
                                    {(item.vehicle_usage || "chantier") === "administratif" ? "Administratif" : "Chantier"}
                                  </Badge>
                                </td>
                              )}
                              {cat.id === "epi" && (
                                <td>
                                  {item.expiry_date ? (
                                    <Badge variant={new Date(item.expiry_date) < new Date() ? "destructive" : "outline"}>
                                      {formatDate(item.expiry_date)}
                                    </Badge>
                                  ) : "-"}
                                </td>
                              )}
                              <td>
                                <div className="flex gap-1">
                                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openDetail(item)} data-testid={`view-equipment-${item.id}`} title="Voir le détail">
                                    <Eye className="h-4 w-4 text-blue-600" />
                                  </Button>
                                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(item)}>
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleArchiveToggle(item)} data-testid={`archive-equipment-${item.id}`} title={item.archived ? "Restaurer" : "Archiver"}>
                                    {item.archived ? <ArchiveRestore className="h-4 w-4 text-emerald-600" /> : <Archive className="h-4 w-4 text-slate-500" />}
                                  </Button>
                                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setItemToDelete(item); setDeleteDialogOpen(true); }}>
                                    <Trash2 className="h-4 w-4 text-destructive" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        ))}

        {/* Inventory Dashboard Tab */}
        <TabsContent value="inventaire">
          {loading ? (
            <div className="flex items-center justify-center h-48"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
          ) : !dashboard ? (
            <div className="text-center py-12 text-muted-foreground">
              <BarChart3 className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>Aucune donnée d'inventaire</p>
            </div>
          ) : (
            <div className="space-y-6" data-testid="inventory-dashboard">
              {/* KPIs */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-5 pb-4">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Total équipements</p>
                    <p className="text-2xl font-heading font-bold mt-1" data-testid="kpi-total-items">{dashboard.total_items}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-5 pb-4">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Valeur totale</p>
                    <p className="text-2xl font-heading font-bold mt-1 font-mono" data-testid="kpi-total-value">{formatCurrency(dashboard.total_value)}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-5 pb-4">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Véhicules</p>
                    <p className="text-2xl font-heading font-bold mt-1" data-testid="kpi-total-vehicles">{dashboard.total_vehicles}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-5 pb-4">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Coûts entretien</p>
                    <p className="text-2xl font-heading font-bold mt-1 font-mono" data-testid="kpi-maintenance-cost">{formatCurrency(dashboard.total_maintenance_cost)}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Charts row */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Pie chart: items by category */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="font-heading text-base">Répartition par catégorie</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {dashboard.by_category.filter(c => c.count > 0).length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-8">Aucune donnée</p>
                    ) : (
                      <ResponsiveContainer width="100%" height={280}>
                        <PieChart>
                          <Pie
                            data={dashboard.by_category.filter(c => c.count > 0)}
                            dataKey="count"
                            nameKey="label"
                            cx="50%"
                            cy="50%"
                            outerRadius={100}
                            label={({ label, count }) => `${label} (${count})`}
                            labelLine={true}
                          >
                            {dashboard.by_category.filter(c => c.count > 0).map((_, i) => (
                              <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(v) => `${v} item(s)`} />
                        </PieChart>
                      </ResponsiveContainer>
                    )}
                  </CardContent>
                </Card>

                {/* Bar chart: value by category */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="font-heading text-base">Valeur par catégorie (EUR)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {dashboard.by_category.filter(c => c.value > 0).length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-8">Aucune donnée</p>
                    ) : (
                      <ResponsiveContainer width="100%" height={280}>
                        <BarChart data={dashboard.by_category.filter(c => c.value > 0)} layout="vertical" margin={{ left: 20 }}>
                          <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                          <XAxis type="number" tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                          <YAxis type="category" dataKey="label" width={110} tick={{ fontSize: 12 }} />
                          <Tooltip formatter={(v) => formatCurrency(v)} />
                          <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                            {dashboard.by_category.filter(c => c.value > 0).map((_, i) => (
                              <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Vehicle maintenance costs */}
              {dashboard.vehicle_costs.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="font-heading text-base">Coûts d'entretien par véhicule</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={Math.max(200, dashboard.vehicle_costs.length * 50)}>
                      <BarChart data={dashboard.vehicle_costs} layout="vertical" margin={{ left: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                        <XAxis type="number" tickFormatter={v => `${v} EUR`} />
                        <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 12 }}
                          tickFormatter={v => {
                            const item = dashboard.vehicle_costs.find(c => c.name === v);
                            return item?.registration ? `${v} (${item.registration})` : v;
                          }}
                        />
                        <Tooltip
                          formatter={(v) => formatCurrency(v)}
                          labelFormatter={(v) => {
                            const item = dashboard.vehicle_costs.find(c => c.name === v);
                            return `${v}${item?.registration ? ` - ${item.registration}` : ""}`;
                          }}
                        />
                        <Bar dataKey="total_cost" fill="#C05621" radius={[0, 4, 4, 0]} name="Coût total" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {/* Recent acquisitions */}
              {dashboard.recent_items.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="font-heading text-base">Dernières acquisitions</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Nom</th>
                            <th>Catégorie</th>
                            <th>Date d'achat</th>
                            <th>Prix</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dashboard.recent_items.map(item => (
                            <tr key={item.id}>
                              <td className="font-medium">{item.name}</td>
                              <td><Badge variant="outline">{item.category}</Badge></td>
                              <td>{formatDate(item.purchase_date)}</td>
                              <td className="font-mono">{item.purchase_price ? formatCurrency(item.purchase_price) : "-"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Alerts recap */}
              {alerts.total_count > 0 && (
                <Card className="border-amber-300">
                  <CardHeader className="pb-2">
                    <CardTitle className="font-heading text-base flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5 text-amber-600" />
                      Alertes en cours ({alerts.total_count})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Type</th>
                            <th>Élément</th>
                            <th>Expiration</th>
                            <th>Statut</th>
                          </tr>
                        </thead>
                        <tbody>
                          {alerts.vehicle_alerts.map((a, i) => (
                            <tr key={`v-${i}`} className={a.days_remaining < 0 ? "bg-red-50" : "bg-amber-50"}>
                              <td><Badge>{a.inspection_type === "technique" ? "CT" : "Antipollution"}</Badge></td>
                              <td>{a.vehicle_name} <span className="font-mono text-xs">({a.registration})</span></td>
                              <td>{formatDate(a.expiry_date)}</td>
                              <td>
                                <Badge variant={a.days_remaining < 0 ? "destructive" : "outline"}>
                                  {a.days_remaining < 0 ? `Expiré (${Math.abs(a.days_remaining)}j)` : `${a.days_remaining}j restants`}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                          {alerts.epi_alerts.map((a, i) => (
                            <tr key={`e-${i}`} className={a.days_remaining < 0 ? "bg-red-50" : "bg-amber-50"}>
                              <td><Badge variant="secondary">EPI</Badge></td>
                              <td>{a.name}</td>
                              <td>{formatDate(a.expiry_date)}</td>
                              <td>
                                <Badge variant={a.days_remaining < 0 ? "destructive" : "outline"}>
                                  {a.days_remaining < 0 ? `Expiré (${Math.abs(a.days_remaining)}j)` : `${a.days_remaining}j restants`}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editingItem ? "Modifier" : "Ajouter"} - {CATEGORIES.find(c => c.id === activeTab)?.label}</DialogTitle>
            <DialogDescription>Remplissez les informations du matériel</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nom *</Label>
                <Input value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} data-testid="eq-name" />
              </div>
              <div className="space-y-2">
                <Label>Marque</Label>
                <Input value={formData.brand} onChange={e => setFormData({...formData, brand: e.target.value})} data-testid="eq-brand" />
              </div>
              <div className="space-y-2">
                <Label>Type de matériel</Label>
                <Input value={formData.material_type} onChange={e => setFormData({...formData, material_type: e.target.value})} data-testid="eq-type" />
              </div>
              <div className="space-y-2">
                <Label>Année</Label>
                <Input type="number" value={formData.year} onChange={e => setFormData({...formData, year: e.target.value})} data-testid="eq-year" />
              </div>
              <div className="space-y-2">
                <Label>N° de série</Label>
                <Input
                  value={formData.serial_number}
                  onChange={e => setFormData({...formData, serial_number: e.target.value})}
                  data-testid="eq-serial"
                  placeholder="Ex : SN-2024-1234"
                />
              </div>
              <div className="space-y-2">
                <Label>Source d'énergie</Label>
                <Select
                  value={formData.power_source || "none"}
                  onValueChange={v => setFormData({
                    ...formData,
                    power_source: v === "none" ? "" : v,
                    // Auto-clear fuel_type if power is not thermal/hybrid
                    fuel_type: (v === "thermique" || v === "hybride") ? formData.fuel_type : "",
                  })}
                >
                  <SelectTrigger data-testid="eq-power-source"><SelectValue placeholder="Non renseigné" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Non renseigné</SelectItem>
                    {POWER_SOURCES.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {(formData.power_source === "thermique" || formData.power_source === "hybride") && (
                <div className="space-y-2">
                  <Label>Carburant *</Label>
                  <Select
                    value={formData.fuel_type || "none"}
                    onValueChange={v => setFormData({...formData, fuel_type: v === "none" ? "" : v})}
                  >
                    <SelectTrigger data-testid="eq-fuel-type"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      {FUEL_TYPES.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-2">
                <Label>Entreprise d'entretien</Label>
                <Input value={formData.maintenance_company} onChange={e => setFormData({...formData, maintenance_company: e.target.value})} data-testid="eq-maintenance" />
              </div>
              <div className="space-y-2">
                <Label>Prix neuf / d'achat (EUR)</Label>
                <Input type="number" step="0.01" value={formData.purchase_price} onChange={e => setFormData({...formData, purchase_price: e.target.value})} data-testid="eq-price" />
              </div>
              <div className="space-y-2">
                <Label>Lieu d'achat</Label>
                <Input value={formData.purchase_location} onChange={e => setFormData({...formData, purchase_location: e.target.value})} data-testid="eq-location" />
              </div>
              <div className="space-y-2">
                <Label>Date d'achat</Label>
                <Input type="date" value={formData.purchase_date} onChange={e => setFormData({...formData, purchase_date: e.target.value})} data-testid="eq-date" />
              </div>
              {(activeTab === "vehicules" || activeTab === "gros_materiel") && (
                <div className="space-y-2">
                  <Label>1<sup>ère</sup> mise en circulation</Label>
                  <Input
                    type="date"
                    value={formData.first_registration_date}
                    onChange={e => setFormData({...formData, first_registration_date: e.target.value})}
                    data-testid="eq-first-registration"
                  />
                </div>
              )}
            </div>

            {/* VAT block — only shown if a price is entered */}
            {formData.purchase_price && (
              <div className="space-y-3 p-3 border rounded-sm bg-slate-50">
                <Label className="text-base font-medium flex items-center gap-2">
                  TVA à l'achat
                </Label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="space-y-2">
                    <Label className="text-xs">Prix saisi</Label>
                    <Select
                      value={formData.purchase_price_is_ttc}
                      onValueChange={v => setFormData({...formData, purchase_price_is_ttc: v})}
                    >
                      <SelectTrigger data-testid="eq-price-ttc"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ttc">TTC</SelectItem>
                        <SelectItem value="ht">HT</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Taux de TVA (%)</Label>
                    <Select
                      value={formData.vat_rate}
                      onValueChange={v => setFormData({...formData, vat_rate: v})}
                    >
                      <SelectTrigger data-testid="eq-vat-rate"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="20">20 %</SelectItem>
                        <SelectItem value="10">10 %</SelectItem>
                        <SelectItem value="5.5">5,5 %</SelectItem>
                        <SelectItem value="2.1">2,1 %</SelectItem>
                        <SelectItem value="0">0 %</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">TVA récupérable ?</Label>
                    <div className="flex items-center gap-2 h-10">
                      <input
                        type="checkbox"
                        id="vat-recoverable"
                        checked={!!formData.vat_recoverable}
                        onChange={e => setFormData({...formData, vat_recoverable: e.target.checked})}
                        className="h-4 w-4"
                        data-testid="eq-vat-recoverable"
                      />
                      <Label htmlFor="vat-recoverable" className="cursor-pointer text-sm">Oui (matériel et entretien)</Label>
                    </div>
                  </div>
                </div>
                {/* Live HT/TTC preview */}
                {(() => {
                  const p = parseFloat(formData.purchase_price) || 0;
                  const r = parseFloat(formData.vat_rate) || 0;
                  if (p <= 0) return null;
                  const ht = formData.purchase_price_is_ttc === "ttc" ? p / (1 + r / 100) : p;
                  const ttc = formData.purchase_price_is_ttc === "ttc" ? p : p * (1 + r / 100);
                  const vat = ttc - ht;
                  return (
                    <div className="text-xs flex items-center gap-4 flex-wrap pt-1 font-mono" data-testid="eq-vat-preview">
                      <span>HT : <strong>{ht.toFixed(2)} €</strong></span>
                      <span>TVA : <strong>{vat.toFixed(2)} €</strong></span>
                      <span>TTC : <strong>{ttc.toFixed(2)} €</strong></span>
                      {formData.vat_recoverable && (
                        <span className="text-green-700">
                          Coût réel (HT) : <strong>{ht.toFixed(2)} €</strong>
                        </span>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}

            {/* EPI specific */}
            {activeTab === "epi" && (
              <div className="space-y-2 p-3 border rounded-sm bg-amber-50">
                <Label>Date de fin de validité</Label>
                <Input type="date" value={formData.expiry_date} onChange={e => setFormData({...formData, expiry_date: e.target.value})} data-testid="eq-expiry" />
              </div>
            )}

            {/* Vehicle specific */}
            {activeTab === "vehicules" && (
              <div className="space-y-4 p-3 border rounded-sm bg-blue-50">
                <Label className="text-base font-medium">Informations véhicule</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2 md:col-span-2">
                    <Label>Catégorie d'usage *</Label>
                    <Select
                      value={formData.vehicle_usage || "chantier"}
                      onValueChange={(v) => setFormData({ ...formData, vehicle_usage: v })}
                    >
                      <SelectTrigger data-testid="eq-vehicle-usage">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="chantier">Véhicule de chantier (visible par les ouvriers)</SelectItem>
                        <SelectItem value="administratif">Véhicule administratif (admin uniquement)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Les véhicules administratifs n'apparaissent pas dans les demandes chantier des ouvriers.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label>Immatriculation</Label>
                    <Input value={formData.registration} onChange={e => setFormData({...formData, registration: e.target.value})} data-testid="eq-registration" placeholder="AA-123-BB" />
                  </div>
                  <div className="space-y-2">
                    <Label>Moteur</Label>
                    <Input value={formData.engine} onChange={e => setFormData({...formData, engine: e.target.value})} data-testid="eq-engine" placeholder="Diesel 2.0 HDi" />
                  </div>
                  <div className="space-y-2">
                    <Label>Pneus avant (dimension)</Label>
                    <Input value={formData.front_tire_size} onChange={e => setFormData({...formData, front_tire_size: e.target.value})} data-testid="eq-front-tire" placeholder="205/55 R16" />
                  </div>
                  <div className="space-y-2">
                    <Label>Pneus arrière (dimension)</Label>
                    <Input value={formData.rear_tire_size} onChange={e => setFormData({...formData, rear_tire_size: e.target.value})} data-testid="eq-rear-tire" placeholder="205/55 R16" />
                  </div>
                </div>
              </div>
            )}

            {/* Battery (vehicles + gros_materiel) */}
            {(activeTab === "vehicules" || activeTab === "gros_materiel") && (
              <div className="space-y-4 p-3 border rounded-sm bg-emerald-50">
                <Label className="text-base font-medium">Batterie</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Référence batterie</Label>
                    <Input
                      value={formData.battery_reference}
                      onChange={e => setFormData({...formData, battery_reference: e.target.value})}
                      data-testid="eq-battery-ref"
                      placeholder="Ex : Varta E11, L2 B19..."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Capacité batterie</Label>
                    <Input
                      value={formData.battery_capacity}
                      onChange={e => setFormData({...formData, battery_capacity: e.target.value})}
                      data-testid="eq-battery-capacity"
                      placeholder="Ex : 74 Ah, 12V 100Ah..."
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label>Commentaires</Label>
              <Textarea value={formData.comments} onChange={e => setFormData({...formData, comments: e.target.value})} data-testid="eq-comments" />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
              <Button type="submit" data-testid="eq-submit">{editingItem ? "Modifier" : "Ajouter"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce matériel ?</AlertDialogTitle>
            <AlertDialogDescription>
              {itemToDelete?.name} sera supprimé définitivement avec tout son historique.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground" data-testid="confirm-delete-eq">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Equipment Detail Dialog (all categories) */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              {(() => {
                const cat = CATEGORIES.find(c => c.id === detailItem?.category);
                const Icon = cat?.icon || Package;
                return <Icon className="h-5 w-5" />;
              })()}
              {detailItem?.name} {detailItem?.registration && <Badge variant="outline" className="font-mono">{detailItem.registration}</Badge>}
            </DialogTitle>
            <DialogDescription>
              {detailItem?.brand}{detailItem?.category === "vehicules" && detailItem?.engine ? ` - ${detailItem.engine}` : ""}
            </DialogDescription>
          </DialogHeader>

          {detailItem && (
            <div className="space-y-6">
              {/* Info block — champs véhicule uniquement pour les véhicules */}
              {detailItem.category === "vehicules" ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                  <div><span className="text-muted-foreground">Pneus avant:</span> <span className="font-medium">{detailItem.front_tire_size || "-"}</span></div>
                  <div><span className="text-muted-foreground">Pneus arrière:</span> <span className="font-medium">{detailItem.rear_tire_size || "-"}</span></div>
                  <div><span className="text-muted-foreground">Entretien:</span> <span className="font-medium">{detailItem.maintenance_company || "-"}</span></div>
                  <div data-testid="detail-serial"><span className="text-muted-foreground">N° de série:</span> <span className="font-medium font-mono">{detailItem.serial_number || "-"}</span></div>
                  <div data-testid="detail-first-reg"><span className="text-muted-foreground">1<sup>ère</sup> mise en circulation:</span> <span className="font-medium">{detailItem.first_registration_date ? formatDate(detailItem.first_registration_date) : "-"}</span></div>
                  <div data-testid="detail-power"><span className="text-muted-foreground">Énergie:</span> <span className="font-medium">{detailItem.power_source ? POWER_LABEL[detailItem.power_source] : "-"}</span></div>
                  <div data-testid="detail-fuel"><span className="text-muted-foreground">Carburant:</span> <span className="font-medium">{detailItem.fuel_type ? FUEL_LABEL[detailItem.fuel_type] : "-"}</span></div>
                  <div data-testid="detail-battery-ref"><span className="text-muted-foreground">Batterie (réf.):</span> <span className="font-medium">{detailItem.battery_reference || "-"}</span></div>
                  <div data-testid="detail-battery-capacity"><span className="text-muted-foreground">Batterie (capacité):</span> <span className="font-medium">{detailItem.battery_capacity || "-"}</span></div>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                  <div><span className="text-muted-foreground">Catégorie:</span> <span className="font-medium">{CATEGORIES.find(c => c.id === detailItem.category)?.label || detailItem.category}</span></div>
                  <div><span className="text-muted-foreground">Marque:</span> <span className="font-medium">{detailItem.brand || "-"}</span></div>
                  <div><span className="text-muted-foreground">N° de série:</span> <span className="font-medium font-mono">{detailItem.serial_number || "-"}</span></div>
                  {detailItem.first_registration_date && (
                    <div><span className="text-muted-foreground">Mise en service:</span> <span className="font-medium">{formatDate(detailItem.first_registration_date)}</span></div>
                  )}
                  {detailItem.expiry_date && (
                    <div><span className="text-muted-foreground">Date d&apos;expiration:</span> <span className="font-medium">{formatDate(detailItem.expiry_date)}</span></div>
                  )}
                  {detailItem.purchase_date && (
                    <div><span className="text-muted-foreground">Achat:</span> <span className="font-medium">{formatDate(detailItem.purchase_date)}</span></div>
                  )}
                  {detailItem.purchase_price != null && detailItem.purchase_price !== "" && (
                    <div><span className="text-muted-foreground">Prix:</span> <span className="font-medium">{formatCurrency(detailItem.purchase_price)}</span></div>
                  )}
                  {detailItem.assigned_to && (
                    <div><span className="text-muted-foreground">Affecté à:</span> <span className="font-medium">{detailItem.assigned_to}</span></div>
                  )}
                </div>
              )}

              {/* Inspections — véhicules uniquement */}
              {detailItem.category === "vehicules" && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-heading font-bold text-base">Contrôles</h3>
                  <Button size="sm" onClick={() => setInspectionDialogOpen(true)} data-testid="add-inspection-btn">
                    <Plus className="h-4 w-4 mr-1" />Contrôle
                  </Button>
                </div>
                {(detailItem.inspections || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground italic text-center py-4 bg-muted/30 rounded">Aucun contrôle enregistré</p>
                ) : (
                  <div className="space-y-2">
                    {detailItem.inspections.map(insp => {
                      const exp = new Date(insp.expiry_date);
                      const now = new Date();
                      const daysLeft = Math.floor((exp - now) / 86400000);
                      const isExpired = daysLeft < 0;
                      const isSoon = daysLeft >= 0 && daysLeft <= 90;
                      return (
                        <div key={insp.id} className={`flex items-center justify-between p-3 rounded border ${isExpired ? "bg-red-50 border-red-200" : isSoon ? "bg-amber-50 border-amber-200" : ""}`}>
                          <div>
                            <div className="flex items-center gap-2">
                              <Badge variant={insp.inspection_type === "technique" ? "default" : "secondary"}>
                                {insp.inspection_type === "technique" ? "CT" : "Antipollution"}
                              </Badge>
                              {isExpired && <Badge variant="destructive">Expiré</Badge>}
                              {isSoon && !isExpired && <Badge className="bg-amber-500">{daysLeft}j restants</Badge>}
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                              Fait le {formatDate(insp.inspection_date)} - Expire le {formatDate(insp.expiry_date)}
                            </p>
                          </div>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={async () => {
                            await api.deleteVehicleInspection(insp.id);
                            const res = await api.getEquipmentItem(detailItem.id);
                            setDetailItem(res.data);
                            fetchItems();
                            toast.success("Contrôle supprimé");
                          }}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              )}

              {/* Maintenance records */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-heading font-bold text-base">Entretiens & Réparations</h3>
                  <Button size="sm" onClick={() => setMaintenanceDialogOpen(true)} data-testid="add-maintenance-btn">
                    <Plus className="h-4 w-4 mr-1" />Entretien
                  </Button>
                </div>
                {(detailItem.maintenance_records || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground italic text-center py-4 bg-muted/30 rounded">Aucun entretien enregistré</p>
                ) : (
                  <div className="space-y-2">
                    {detailItem.maintenance_records.map(rec => {
                      const nextDate = rec.next_maintenance_date;
                      let nextBadge = null;
                      if (nextDate) {
                        const daysUntil = Math.floor((new Date(nextDate) - new Date()) / 86400000);
                        if (daysUntil < 0) {
                          nextBadge = <Badge variant="destructive" className="text-xs" data-testid={`maint-next-overdue-${rec.id}`}>Prochain en retard ({Math.abs(daysUntil)}j)</Badge>;
                        } else if (daysUntil <= 30) {
                          nextBadge = <Badge className="bg-amber-500 text-white text-xs" data-testid={`maint-next-soon-${rec.id}`}>Prochain dans {daysUntil}j</Badge>;
                        } else {
                          nextBadge = <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-300" data-testid={`maint-next-${rec.id}`}>Prochain : {formatDate(nextDate)}</Badge>;
                        }
                      }
                      return (
                      <div key={rec.id} className="flex items-center justify-between p-3 rounded border">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium text-sm">{rec.description}</p>
                            {nextBadge}
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {formatDate(rec.maintenance_date)} {rec.location && `- ${rec.location}`}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="text-right font-mono text-sm">
                            <div className="font-bold">
                              {formatCurrency(rec.cost)}
                              <span className="text-xs text-muted-foreground ml-1">
                                {rec.cost_is_ttc === false ? "HT" : rec.cost_is_ttc === true ? "TTC" : ""}
                              </span>
                            </div>
                            {detailItem.vat_recoverable && rec.cost && rec.vat_rate != null && rec.cost_is_ttc !== false && (
                              <div className="text-xs text-green-700" data-testid={`maint-ht-${rec.id}`}>
                                = {formatCurrency(rec.cost / (1 + (rec.vat_rate || 0) / 100))} HT récup.
                              </div>
                            )}
                          </div>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={async () => {
                            await api.deleteVehicleMaintenance(rec.id);
                            const res = await api.getEquipmentItem(detailItem.id);
                            setDetailItem(res.data);
                            toast.success("Entretien supprimé");
                          }}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                        </div>
                      </div>
                      );
                    })}
                    <div className="text-right text-sm font-bold pt-2 border-t">
                      Total: {formatCurrency(detailItem.maintenance_records.reduce((s, r) => s + (r.cost || 0), 0))}
                      {detailItem.vat_recoverable && (
                        <div className="text-xs text-green-700 font-normal mt-1" data-testid="maint-total-ht">
                          Total HT récupérable : {formatCurrency(detailItem.maintenance_records.reduce((s, r) => {
                            if (!r.cost) return s;
                            if (r.cost_is_ttc === false) return s + r.cost;
                            if (r.vat_rate != null) return s + r.cost / (1 + r.vat_rate / 100);
                            return s + r.cost;
                          }, 0))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Phase 187 — Fournisseurs & Prestataires associés */}
              <LinkedSuppliersBlock
                equipmentId={detailItem.id}
                refreshKey={(detailItem.maintenance_records || []).length}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Inspection Dialog */}
      <Dialog open={inspectionDialogOpen} onOpenChange={setInspectionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Ajouter un contrôle</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddInspection} className="space-y-4">
            <div className="space-y-2">
              <Label>Type de contrôle</Label>
              <Select value={inspForm.inspection_type} onValueChange={v => setInspForm({...inspForm, inspection_type: v})}>
                <SelectTrigger data-testid="insp-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="technique">Contrôle technique</SelectItem>
                  <SelectItem value="antipollution">Contrôle antipollution</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date du contrôle</Label>
                <Input type="date" value={inspForm.inspection_date} onChange={e => setInspForm({...inspForm, inspection_date: e.target.value})} required data-testid="insp-date" />
              </div>
              <div className="space-y-2">
                <Label>Date fin de validité</Label>
                <Input type="date" value={inspForm.expiry_date} onChange={e => setInspForm({...inspForm, expiry_date: e.target.value})} required data-testid="insp-expiry" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Input value={inspForm.notes} onChange={e => setInspForm({...inspForm, notes: e.target.value})} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setInspectionDialogOpen(false)}>Annuler</Button>
              <Button type="submit" data-testid="submit-inspection">Ajouter</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Maintenance Dialog */}
      <Dialog open={maintenanceDialogOpen} onOpenChange={setMaintenanceDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Ajouter un entretien / réparation</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddMaintenance} className="space-y-4">
            <div className="space-y-2">
              <Label>Description *</Label>
              <Input value={maintForm.description} onChange={e => setMaintForm({...maintForm, description: e.target.value})} required placeholder="Vidange, pneus, réparation..." data-testid="maint-desc" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date</Label>
                <Input type="date" value={maintForm.maintenance_date} onChange={e => setMaintForm({...maintForm, maintenance_date: e.target.value})} required data-testid="maint-date" />
              </div>
              <div className="space-y-2">
                <Label>Coût (EUR)</Label>
                <Input type="number" step="0.01" value={maintForm.cost} onChange={e => setMaintForm({...maintForm, cost: e.target.value})} data-testid="maint-cost" />
              </div>
            </div>
            {maintForm.cost && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-3 border rounded-sm bg-slate-50">
                <div className="space-y-2">
                  <Label className="text-xs">Prix saisi</Label>
                  <Select value={maintForm.cost_is_ttc} onValueChange={v => setMaintForm({...maintForm, cost_is_ttc: v})}>
                    <SelectTrigger data-testid="maint-cost-ttc"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ttc">TTC</SelectItem>
                      <SelectItem value="ht">HT</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Taux TVA (%)</Label>
                  <Select value={maintForm.vat_rate} onValueChange={v => setMaintForm({...maintForm, vat_rate: v})}>
                    <SelectTrigger data-testid="maint-vat-rate"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="20">20 %</SelectItem>
                      <SelectItem value="10">10 %</SelectItem>
                      <SelectItem value="5.5">5,5 %</SelectItem>
                      <SelectItem value="0">0 %</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label>Lieu</Label>
              <Input value={maintForm.location} onChange={e => setMaintForm({...maintForm, location: e.target.value})} placeholder="Garage, concessionnaire..." data-testid="maint-location" />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Input value={maintForm.notes} onChange={e => setMaintForm({...maintForm, notes: e.target.value})} />
            </div>
            <div className="space-y-2 p-3 border rounded-sm bg-amber-50/60">
              <Label className="text-xs font-medium text-amber-900 flex items-center gap-1">
                <AlertTriangle className="h-3.5 w-3.5" /> Prochain entretien prévu le (rappel automatique)
              </Label>
              <Input
                type="date"
                value={maintForm.next_maintenance_date}
                onChange={e => setMaintForm({...maintForm, next_maintenance_date: e.target.value})}
                data-testid="maint-next-date"
              />
              <p className="text-xs text-amber-900/70">
                Le matériel apparaîtra dans les alertes 30 jours avant cette date. Laisser vide si l&apos;entretien n&apos;est pas récurrent.
              </p>
            </div>
            <MaintenanceCrossLinksFields
              value={{ supplier_id: maintForm.supplier_id, supplier_product_id: maintForm.supplier_product_id, reason_id: maintForm.reason_id }}
              onChange={(patch) => setMaintForm({ ...maintForm, ...patch })}
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setMaintenanceDialogOpen(false)}>Annuler</Button>
              <Button type="submit" data-testid="submit-maintenance">Ajouter</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EquipmentPage;
